<template>
  <button
    :type="submit ? 'submit' : 'button'"
    class="amaiz-icon-button"
    :class="[className, type, iconType, size, { loading: isLoading, transparent }]"
    :disabled="disabled"
    data-testid="button-icon"
    :data-test-icon="icon?.name"
    @click="clickHandler($event as PointerEvent)">
    <div :class="{ 'opacity-0': isLoading }">
      <slot>
        <SvgIcon
          v-if="icon"
          :name="icon.name"
          :size="iconSize"
          :type="icon.type"/>
      </slot>
    </div>
    <LoaderIcon
      v-if="isLoading"
      :size="iconSize"
      color="blue"
      class="absolute"/>
  </button>
</template>

<script lang="ts" setup generic="T extends IconType = 'sized', S extends IconSize = 24">
import { computed, ref } from 'vue';

import { IconProps } from '@/_redesign/shared/types/IconProps';
import { IconSize } from '@/_redesign/shared/types/IconSize';
import { IconType } from '@/_redesign/shared/types/IconType';
import LoaderIcon from '@/_redesign/shared/ui/LoaderIcon/LoaderIcon.vue';
import SvgIcon from '@/_redesign/shared/ui/SvgIcon/SvgIcon.vue';
import { VueClass } from '@/types/VueClass';

const {
  icon,
  iconType = 'primary',
  size = 'large',
  transparent = false,
  class: className = '',
  submit = false,
  disabled = false,
  loading = false,
  onClick,
} = defineProps<{
  icon?: IconProps<'sized' | 'core'>;
  type?: 'circle' | 'square';
  iconType?: 'primary' | 'neutral' | 'danger';
  size?: 'large' | 'medium' | 'small' | 'tiny';
  transparent?: boolean;
  class?: VueClass;
  submit?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: ((payload: PointerEvent) => void | Promise<void>);
}>();

const innerLoading = ref(false);

const isLoading = computed(() => (loading || innerLoading.value) && !disabled);

const iconSize = computed<IconSize>(() => {
  if (size === 'tiny') return 12;
  if (size === 'small') return 16;
  if (size === 'medium') return 16;
  return 24;
});

async function clickHandler(payload: PointerEvent) {
  if (loading || disabled) return;
  innerLoading.value = true;
  await onClick?.(payload);
  innerLoading.value = false;
}
</script>

<style lang="scss" scoped>
.amaiz-icon-button {
  @apply flex items-center justify-center shrink-0 rounded-full;
  @apply transition-colors duration-300;

  &.large {
    @apply w-48 h-48;
  }

  &.medium {
    @apply w-40 h-40;
  }

  &.small {
    @apply w-32 h-32;
  }

  &.tiny {
    @apply w-24 h-24;
  }

  &.square {
    &.large {
      @apply rounded-16;
    }

    &.medium {
      @apply rounded-12;
    }

    &.small,
    &.tiny {
      @apply rounded-8;
    }
  }

  &.primary {
    @apply text-accent-primary-default;
  }

  &.neutral {
    @apply text-pending-default;
  }

  &.danger {
    @apply text-negative-default;
  }

  &:not(:disabled) {
    @apply bg-accent-secondary-default hover:bg-accent-secondary-hover active:bg-accent-secondary-selected;

    &.loading {
      @apply bg-accent-secondary-selected;
    }

    &.is-active {
      @apply bg-accent-secondary-selected;
    }
  }

  &:disabled {
    @apply text-neutral-secondary-disabled bg-neutral-secondary-disabled;
  }

  &.transparent {
    @apply bg-transparent hover:bg-neutral-tertiary-hover active:bg-neutral-tertiary-selected disabled:bg-transparent;

    &.loading {
      @apply bg-neutral-tertiary-selected;
    }
  }
}
</style>
